import Box from "@mui/material/Box";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { X } from "lucide-mui";
import { useState, type ReactNode, type UIEvent } from "react";
import { SHORTCUT_SCOPE, useKeyboardShortcuts } from "../hooks/useKeyboardShortcuts.ts";
import { useResizableWidth } from "../hooks/useResizableWidth.ts";
import { useWindowWidth } from "../hooks/useWindowWidth.ts";
import PanelResizer from "./PanelResizer.tsx";

// One width for every detail drawer, saved per user; the page keeps a strip
// of at least MAX_WIDTH_GUTTER beside it.
const WIDTH_PREFERENCE = "detail_drawer.width";
const DEFAULT_WIDTH = 640;
const MIN_WIDTH = 480;
const MAX_WIDTH_GUTTER = 160;
// The paper's cap on a window narrower than its width (see its sx).
const EDGE_GUTTER = 48;
// Below MUI's `sm` the drawer fills the screen.
const SM = 600;

export type DetailDrawerCloseReason = "escapeKeyDown" | "backdropClick" | "closeButton";

/** A panel docked to the drawer's left edge (a contact opened from it). */
export interface DrawerAside {
  /** Shown: the drawer's J / K rest and it shares the focus. */
  open: boolean;
  /** Rendered beside the drawer; `edge` is how far its left edge sits from the window's right. */
  render: (edge: number) => ReactNode;
}

/**
 * Title, actions and keys stay on top while the body scrolls under them;
 * once something has, a divider under the header marks the edge.
 */
const headerSx = (scrolled: boolean) =>
  ({
    position: "sticky",
    top: 0,
    zIndex: 2,
    mx: -3,
    px: 3,
    pt: 3,
    pb: 2,
    mb: 1,
    bgcolor: "background.paper",
    borderBottom: 1,
    borderColor: scrolled ? "divider" : "transparent",
    transition: "border-color 150ms",
  }) as const;

interface DetailDrawerProps {
  open: boolean;
  onClose: (reason: DetailDrawerCloseReason) => void;
  /** Small grey line over the title (the kind of thing, its context). */
  overline?: ReactNode;
  title: ReactNode;
  /** The row of controls under the title (decisions, buttons). */
  actions?: ReactNode;
  /** Keyboard hints under the actions. */
  hints?: ReactNode;
  /** The row after / before this one (J / ↓, K / ↑). */
  onStep?: (direction: 1 | -1) => void;
  /** A panel that opens out of its left edge; the drawer itself never moves. */
  aside?: DrawerAside;
  children: ReactNode;
}

/**
 * The right-hand drawer a list page opens a row in: a sticky header with the
 * title, actions and keys, the body under it. It carries `SHORTCUT_SCOPE`, so
 * the page's single-key shortcuts work while it has focus; a dialog opened over
 * it keeps its own keys. Sits above the app bar. Its left edge drags to
 * resize it (double-click resets). An `aside` docks to that edge.
 */
export default function DetailDrawer({
  open,
  onClose,
  overline,
  title,
  actions,
  hints,
  onStep,
  aside,
  children,
}: DetailDrawerProps) {
  const [scrolled, setScrolled] = useState(false);
  const { width, resizer } = useResizableWidth({
    preference: WIDTH_PREFERENCE,
    defaultWidth: DEFAULT_WIDTH,
    min: MIN_WIDTH,
    max: Math.max(MIN_WIDTH, window.innerWidth - MAX_WIDTH_GUTTER),
  });
  const windowWidth = useWindowWidth();
  // Where its left edge sits, as drawn (the cap in the paper's sx).
  const edge = windowWidth < SM ? windowWidth : Math.min(width, windowWidth - EDGE_GUTTER);
  const asideOpen = !!aside?.open;
  const step = (direction: 1 | -1) => () => onStep?.(direction);
  useKeyboardShortcuts(
    { j: step(1), ArrowDown: step(1), k: step(-1), ArrowUp: step(-1) },
    open && !!onStep && !asideOpen,
  );
  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={(_event, reason) => onClose(reason)}
        // The aside sits outside this modal: focus may go there while it is open.
        disableEnforceFocus={asideOpen}
        sx={{ zIndex: (theme) => theme.zIndex.modal }}
        slotProps={{
          paper: {
            ...SHORTCUT_SCOPE,
            // No dark-mode elevation tint: the sticky header's paper matches the
            // drawer's. The body scrolls, not the paper, so the resize handle stays.
            sx: { backgroundImage: "none", overflow: "hidden" },
          },
        }}
      >
        <PanelResizer
          {...resizer}
          label="Resize the panel"
          sx={{
            position: "absolute",
            top: 0,
            bottom: 0,
            left: 0,
            zIndex: 3,
            display: { xs: "none", sm: "flex" },
          }}
        />
        <Box
          onScroll={(e: UIEvent<HTMLDivElement>) => setScrolled(e.currentTarget.scrollTop > 0)}
          sx={{
            // The cap keeps it on screen when the window shrinks under a saved width.
            width: { xs: "100vw", sm: `min(${width}px, 100vw - 48px)` },
            flexGrow: 1,
            minHeight: 0,
            overflowY: "auto",
            px: 3,
            pb: 3,
          }}
        >
          <Stack spacing={1.5} sx={headerSx(scrolled)}>
            <Stack direction="row" sx={{ alignItems: "flex-start" }}>
              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                {overline && (
                  <Typography variant="overline" sx={{ color: "text.secondary" }}>
                    {overline}
                  </Typography>
                )}
                <Typography variant="h6">{title}</Typography>
              </Box>
              <IconButton onClick={() => onClose("closeButton")} aria-label="Close">
                <X />
              </IconButton>
            </Stack>
            {actions}
            {hints}
          </Stack>
          {children}
        </Box>
      </Drawer>
      {aside?.render(edge)}
    </>
  );
}
